import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCopyToClipboard } from "@pexip-engage/utils";
import { ErrorResponse, isRouteErrorResponse, useRouteError } from "@remix-run/react";
import * as Sentry from "@sentry/remix";

import { SecondaryButton } from "~/components/Button";
import { ClipboardStatusIcon } from "~/components/Icons/ClipboardStatusIcon";
import { ErrorIcon } from "~/components/Icons/ErrorIcon";
import { PageTitle } from "~/components/PageTitle";
import { Text } from "~/components/Text";
import { sendMessage } from "~/hooks/useSendMessage";
import { useRootLoaderData } from "~/root";
import {
  errors,
  type IApiError,
  type IConfigurationError,
  type IInvalidAppointmentStateError,
  type INoResultsError,
  type INotFoundError,
  type INotSchedulableError,
  type IValidationError,
} from "~/utils/validators.utils";

export function AppErrorBoundary() {
  const { t } = useTranslation();
  const error = useRouteError();
  const isRouteError = isRouteErrorResponse(error);

  const element = isRouteError ? (
    <AppCatchBoundary error={error} />
  ) : (
    <ErrorBoundary error={error} />
  );

  const neutralFallback = t("errors.neutralFallback");
  if (!!neutralFallback) {
    return <NeutralErrorBoundary errorElement={element} html={neutralFallback} />;
  }

  return element;
}

function NeutralErrorBoundary({ errorElement, html }: { errorElement: JSX.Element; html: string }) {
  const data = useRootLoaderData();

  return (
    <div className="flex flex-col gap-sm" data-component="NeutralErrorBoundary">
      <div dangerouslySetInnerHTML={{ __html: html }} data-component="NeutralErrorBoundaryInfo" />
      {data?.sessionId ? <code className="text-xs">Error ID: {data.sessionId}</code> : null}
      <div className="hidden">{errorElement}</div>
    </div>
  );
}

function ErrorBoundary({ error }: { error: unknown }) {
  if (error instanceof Error) {
    // client side error
    Sentry.captureException(error);
  }

  return (
    <ErrorContainer>
      <ErrorMessage>
        <Text i18nKey="errors.500" />
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function AppCatchBoundary({ error }: { error: ErrorResponse }) {
  const { data } = error;

  if (errors.api.is(data)) return <ApiCatchBoundary {...data} />;
  if (errors.configuraturation.is(data)) return <ConfigurationCatchBoundary {...data} />;
  if (errors.invalidAppointmentState.is(data))
    return <InvalidAppointmentStateCatchBoundary {...data} />;
  if (errors.noResults.is(data)) return <NoResultsBoundary {...data} />;
  if (errors.notFound.is(data)) return <NotFoundCatchBoundary {...data} />;
  if (errors.notSchedulable.is(data)) return <NotSchedulableCatchBoundary {...data} />;
  if (errors.unauthorized.is(data)) return <DefaultCatchBoundary error={error} />;
  if (errors.validation.is(data)) return <ValidationCatchBoundary {...data} />;

  return <DefaultCatchBoundary error={error} />;
}

function DefaultCatchBoundary({ error }: { error: ErrorResponse }) {
  useEffect(() => {
    captureException({
      type: "DefaultCatchBoundary",
      errorMessage: "DefaultCatchBoundary",
      extra: error,
    });
  }, [error]);

  return (
    <ErrorContainer>
      <ErrorMessage>
        <Text i18nKey="errors.500" />
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function NoResultsBoundary(error: INoResultsError) {
  const { t } = useTranslation("common", { lng: "en" });
  const value = t(error.translation.key);

  useEffect(() => {
    captureException({ type: error.type, errorMessage: value });
  }, [error, value]);

  return (
    <WarningContainer>
      <ErrorMessage>
        <Text i18nKey={error.translation.key} />
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </WarningContainer>
  );
}

function ApiCatchBoundary(error: IApiError) {
  useEffect(() => {
    captureException({ type: error.type, errorMessage: error.url, extra: error });
  }, [error]);

  return (
    <ErrorContainer>
      <ErrorMessage>
        <Text i18nKey="errors.500" />
      </ErrorMessage>

      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function ConfigurationCatchBoundary(error: IConfigurationError) {
  const { t } = useTranslation("common", { lng: "en" });
  const value = t(error.translation.key, error.translation.interpolation);

  useEffect(() => {
    captureException({ type: error.type, errorMessage: value, extra: error });
  }, [error, value]);

  return (
    <ErrorContainer>
      <ErrorMessage>
        <Text i18nKey={error.translation.key} values={error.translation.interpolation} />
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function NotSchedulableCatchBoundary(error: INotSchedulableError) {
  const { t } = useTranslation("common", { lng: "en" });
  const value = t("errors.configuration.notSchedulable");

  useEffect(() => {
    captureException({ type: error.type, errorMessage: value, extra: error });
  }, [error, value]);

  return (
    <ErrorContainer>
      <ErrorMessage>
        <Text i18nKey={"errors.configuration.notSchedulable"} />
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function ValidationCatchBoundary({ translation, type, ...error }: IValidationError) {
  const { t } = useTranslation("common", { lng: "en" });
  const value = t(translation.key);

  useEffect(() => {
    captureException({ type, errorMessage: value, extra: error });
  }, [error, type, value]);

  return (
    <ErrorContainer>
      <ErrorMessage>
        <Text i18nKey={translation.key} />
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function NotFoundCatchBoundary(error: INotFoundError) {
  const { t } = useTranslation("common", { lng: "en" });
  const value = error.translation
    ? t(error.translation.key, error.translation.interpolations ?? {})
    : t("errors.generic");

  useEffect(() => {
    captureException({ type: error.type, errorMessage: value, extra: error });
  }, [error, value]);

  useEffect(() => {
    if (error.eventType) {
      sendMessage({ type: error.eventType, payload: value });
    }
  }, [error.eventType, value]);

  return (
    <ErrorContainer>
      <ErrorMessage>
        {error.translation ? (
          <div className="flex items-center justify-between">
            {error.translation && (
              <span>
                <Text i18nKey={error.translation.key} values={error.translation.interpolations} />
              </span>
            )}
          </div>
        ) : (
          t("errors.generic")
        )}
      </ErrorMessage>
      <TechnicalInformation error={error} />
    </ErrorContainer>
  );
}

function InvalidAppointmentStateCatchBoundary(error: IInvalidAppointmentStateError) {
  // Dont report InvalidAppointmentState, can happen without being an issue.

  // const { t } = useTranslation("common", { lng: "en" });
  // const value = t(error.translation?.key ?? "errors.generic");

  // useEffect(() => {
  //   captureException({ type: error.type, errorMessage: value, extra: error });
  // }, [error, value]);

  return (
    <div className="py-xl">
      <PageTitle data-variant="InvalidAppointmentState">
        <Text i18nKey={error.translation?.key ?? "errors.generic"} />
      </PageTitle>
    </div>
  );
}

function ErrorContainer({ children }: { children: ReactNode }) {
  return (
    <div
      className="grid gap-sm self-start rounded-lg border border-error-200 bg-error-50 p-md text-error-700"
      data-component="CatchBoundary"
    >
      {children}
    </div>
  );
}

function ErrorMessage({ children }: { children: ReactNode }) {
  const data = useRootLoaderData();
  const [status, copyToClipboard] = useCopyToClipboard();

  return (
    <>
      <div className="flex flex-wrap items-center gap-xs">
        <ErrorIcon />
        <div className="flex-1">{children}</div>
      </div>
      <div className="flex items-center gap-xs">
        <code className="text-sm">ID: {data?.sessionId}</code>
        <SecondaryButton
          onPress={() => data?.sessionId && copyToClipboard(data?.sessionId)}
          size="SMALL"
        >
          <ClipboardStatusIcon status={status} />
        </SecondaryButton>
      </div>
    </>
  );
}

function WarningContainer({ children }: { children: ReactNode }) {
  return (
    <div
      className="grid gap-sm self-start rounded-lg border border-warning p-md text-warning"
      data-component="WarningBoundary"
    >
      {children}
    </div>
  );
}

function TechnicalInformation({ error }: { error: unknown }) {
  if (ENV.IS_PROD) {
    return null;
  }

  return (
    <div className="prose">
      <pre>{JSON.stringify(error, null, 2)}</pre>
    </div>
  );
}

function captureException({
  errorMessage,
  extra,
  fingerprints = [],
  type,
}: {
  type: string;
  errorMessage: string;
  extra?: any;
  fingerprints?: string[];
}) {
  Sentry.withScope(function (scope) {
    scope.setLevel("error");
    scope.setTag("pexip.error.type", type);
    scope.setFingerprint(["{{ default }}", type, ...fingerprints]);

    Sentry.captureException(new Error(`${type}: ${errorMessage}`), { extra });
  });
}
